import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import PasswordRoundedIcon from "@mui/icons-material/PasswordRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import { toast } from "react-toastify";
import api from "./Api";

export default function PasswordResetDialog({ open, onClose, initialUsername = "" }) {
  const [username, setUsername] = useState(initialUsername);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setUsername(initialUsername || "");
  }, [open, initialUsername]);

  const reset = () => {
    setOtp("");
    setNewPassword("");
    setOtpSent(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const requestOtp = async () => {
    if (!username.trim()) {
      toast.error("Enter username");
      return;
    }
    try {
      setLoading(true);
      await api.post("/api/auth/password-reset/request", { username: username.trim() });
      setOtpSent(true);
      toast.success("OTP sent on Telegram");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async () => {
    if (!username.trim() || !otp.trim() || !newPassword) {
      toast.error("Username, OTP and new password are required");
      return;
    }
    try {
      setLoading(true);
      await api.post("/api/auth/password-reset/confirm", {
        username: username.trim(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      toast.success("Password reset successfully");
      close();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          background: "rgba(255,255,255,0.96)",
          boxShadow: "0 28px 70px rgba(0,0,0,0.28)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <LockResetRoundedIcon sx={{ color: "#006a71" }} />
          <Typography component="span" sx={{ fontSize: 22, fontWeight: 900 }}>
            Reset password
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ color: "#5c6b72", fontSize: 14, mb: 2 }}>
          Telegram OTP is required before setting a new password.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {otpSent && (
            <>
              <TextField
                label="Telegram OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                fullWidth
                size="small"
                inputProps={{ inputMode: "numeric" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PasswordRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} sx={{ textTransform: "none", fontWeight: 800 }}>
          Cancel
        </Button>
        {!otpSent ? (
          <Button
            variant="contained"
            onClick={requestOtp}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 900, backgroundColor: "#006a71" }}
          >
            Send OTP
          </Button>
        ) : (
          <>
            <Button onClick={requestOtp} disabled={loading} sx={{ textTransform: "none", fontWeight: 800 }}>
              Resend
            </Button>
            <Button
              variant="contained"
              onClick={confirmReset}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 900, backgroundColor: "#006a71" }}
            >
              Reset
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
